import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  BarChart3, TrendingUp, TrendingDown, ShoppingCart, Package,
  IndianRupee, Eye, Users, ArrowUpRight, ArrowDownRight, Loader2,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorAnalytics() {
  const { vendor, token } = useVendor();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [analytics, setAnalytics] = useState(null);
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Authentication is handled by VendorProtectedRoute
    if (vendor && token) {
      fetchAnalytics();
    }
  }, [vendor, token, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const stats = analytics?.stats || {};

  // Generate mock chart data based on period
  const generateChartData = () => {
    const days = period === 'week' ? 7 : period === 'month' ? 30 : 12;
    return Array.from({ length: Math.min(days, 12) }, (_, i) => ({
      day: i + 1,
      value: Math.floor(Math.random() * 10000) + 1000
    }));
  };

  const chartData = generateChartData();
  const maxValue = Math.max(...chartData.map(d => d.value));

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-analytics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-blue-600" />
              Analytics
            </h1>
            <p className="text-gray-500">Track your store performance and growth</p>
          </div>
          <div className="flex gap-2">
            {['week', 'month', 'year'].map(p => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(p)}
                className={period === p ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'This Year'}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Total Revenue</span>
                    <IndianRupee className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.total_revenue || 0)}</p>
                  <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>+12.5% vs last {period}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Total Orders</span>
                    <ShoppingCart className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_orders || 0}</p>
                  <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>+8.3% vs last {period}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Products Sold</span>
                    <Package className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_products || 0}</p>
                  <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>+5 new items</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Avg Order Value</span>
                    <TrendingUp className="w-4 h-4 text-orange-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPrice(stats.total_orders ? (stats.total_revenue / stats.total_orders) : 0)}
                  </p>
                  <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                    <ArrowDownRight className="w-4 h-4" />
                    <span>-2.1% vs last {period}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sales Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Sales Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-end justify-around gap-2 border-b border-l relative">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400 -ml-10 py-2">
                    <span>₹{(maxValue).toLocaleString()}</span>
                    <span>₹{(maxValue * 0.75).toLocaleString()}</span>
                    <span>₹{(maxValue * 0.5).toLocaleString()}</span>
                    <span>₹{(maxValue * 0.25).toLocaleString()}</span>
                    <span>₹0</span>
                  </div>
                  {/* Bars */}
                  {chartData.map((data, i) => (
                    <div
                      key={i}
                      className="flex-1 max-w-12 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t hover:from-blue-600 hover:to-blue-500 transition-all cursor-pointer group relative"
                      style={{ height: `${(data.value / maxValue) * 100}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        ₹{data.value.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
                {/* X-axis labels */}
                <div className="flex justify-around mt-2 text-xs text-gray-500">
                  {chartData.map((_, i) => (
                    <span key={i}>
                      {period === 'week' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i] :
                       period === 'month' ? `${i + 1}` : 
                       ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Order Status & Top Products */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Order Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Order Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                        <span className="text-gray-700">Pending</span>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800">{stats.pending_orders || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full" />
                        <span className="text-gray-700">Processing</span>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800">{stats.processing_orders || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-indigo-500 rounded-full" />
                        <span className="text-gray-700">Shipped</span>
                      </div>
                      <Badge className="bg-indigo-100 text-indigo-800">{stats.shipped_orders || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                        <span className="text-gray-700">Delivered</span>
                      </div>
                      <Badge className="bg-green-100 text-green-800">{stats.delivered_orders || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Order Fulfillment Rate</span>
                        <span className="font-bold text-green-600">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '95%' }} />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Return Rate</span>
                        <span className="font-bold text-blue-600">2.5%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '2.5%' }} />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Customer Satisfaction</span>
                        <span className="font-bold text-purple-600">4.8/5</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-500 h-2 rounded-full" style={{ width: '96%' }} />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Response Time</span>
                        <span className="font-bold text-orange-600">2.3 hrs</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tips Card */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Tips to Improve Your Sales</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Add high-quality images to your products</li>
                      <li>• Offer competitive pricing and discounts</li>
                      <li>• Keep your inventory updated to avoid stockouts</li>
                      <li>• Respond quickly to customer inquiries</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </VendorLayout>
  );
}
